import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, AlertTriangle, Users, Shield, Loader2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const AdminAccountTypes = () => {
  const { user } = useAuth();
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const isCompanyAdmin = user?.role === 'company_admin';
  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  // Check access permissions for company admins
  useEffect(() => {
    const checkPermissions = async () => {
      if (isCompanyAdmin) {
        try {
          const res = await api.get('/settings/security-permissions');
          if (res.data.company_admin_manage_account_types === false) {
            setHasAccess(false);
          }
        } catch (error) {
          console.error('Failed to check permissions:', error);
        }
      }
      setCheckingAccess(false);
    };
    
    checkPermissions();
  }, [isCompanyAdmin]);

  useEffect(() => {
    if (hasAccess && !checkingAccess) {
      fetchAccountTypes();
    }
  }, [hasAccess, checkingAccess]);

  const fetchAccountTypes = async () => {
    try {
      const res = await api.get('/admin/account-types');
      setAccountTypes(res.data);
    } catch (error) {
      toast.error('Failed to load account types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingType) {
        await api.put(`/admin/account-types/${editingType.id}`, formData);
        toast.success('Account type updated');
      } else {
        await api.post('/admin/account-types', formData);
        toast.success('Account type created');
      }
      setShowModal(false);
      setEditingType(null);
      setFormData({ name: '', description: '' });
      fetchAccountTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (accountType, confirm = false) => {
    // If there are users and no confirmation yet, show the modal
    if (!confirm && accountType.user_count > 0) {
      setDeleteConfirm(accountType);
      return;
    }

    try {
      // Always pass confirm=true for actual deletion (even for 0 users)
      await api.delete(`/admin/account-types/${accountType.id}?confirm=true`);
      toast.success('Account type deleted');
      setDeleteConfirm(null);
      fetchAccountTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({ name: type.name, description: type.description || '' });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  // Show loading while checking permissions
  if (checkingAccess) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Sidebar>
    );
  }

  // Show access denied for company admins without permission
  if (!hasAccess) {
    return (
      <Sidebar>
        <div className="p-8 bg-gray-50/50 min-h-screen">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-lg mx-auto mt-20">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800">Access Denied</h2>
            <p className="text-red-600 mt-2">
              You do not have permission to access Account Types management.
            </p>
            <p className="text-sm text-red-500 mt-3">
              Please contact your Main Administrator if you need access to this feature.
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Account Types</h1>
            <p className="text-slate-600 mt-1">Manage the account types available during registration</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition"
            data-testid="add-account-type"
          >
            <Plus className="w-4 h-4" />
            Add Type
          </button>
        </div>

        {/* Account Types List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : accountTypes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No account types configured</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Description</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Users</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="text-center px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {accountTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-slate-50" data-testid={`account-type-${type.id}`}>
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-800">{type.name}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {type.description || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Users className="w-4 h-4" />
                        {type.user_count || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        type.is_active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEditModal(type)}
                          className="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                          title="Edit"
                          data-testid={`edit-type-${type.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                          data-testid={`delete-type-${type.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h2 className="text-xl font-bold text-slate-800 mb-4">
                {editingType ? 'Edit Account Type' : 'Add Account Type'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g., Buyer"
                    data-testid="account-type-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Brief description of this account type"
                    data-testid="account-type-description"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
                    data-testid="save-account-type"
                  >
                    {editingType ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 text-amber-600 mb-4">
                <AlertTriangle className="w-6 h-6" />
                <h2 className="text-xl font-bold">Confirm Delete</h2>
              </div>
              <p className="text-slate-600 mb-4">
                <strong>{deleteConfirm.user_count}</strong> user{deleteConfirm.user_count !== 1 ? 's' : ''} have 
                the "{deleteConfirm.name}" account type. Deleting this will remove the account type association 
                from these users.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Are you sure you want to proceed?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm, true)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  data-testid="confirm-delete"
                >
                  Delete Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminAccountTypes;
